import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;

const initialState = {
  items: [],
  userRestaurants: [],
  unAssignedUsers: [],
  status: "idle",
  assignStatus: "idle",
  error: null,
  loading: false,
};

export const addUser = createAsyncThunk(
  "user/addUser",
  async ({ body, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/auth/register`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyUser = createAsyncThunk(
  "user/modifyUser",
  async ({ body, userId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/auth/${userId}`,
        body,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addUserFromPermission = createAsyncThunk(
  "user/addUserFromPermission",
  async (body) => {
    try {
      const response = await axios.post(
        `${apiUrl}/auth/create`,
        body,
        getAuthHeaders(false)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const modifyUserFromPermission = createAsyncThunk(
  "user/modifyUserFromPermission",
  async ({ body, userId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/auth/${userId}`,
        body,
        getAuthHeaders(false)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const blockUser = createAsyncThunk(
  "user/blockUser",
  async ({ userId, restaurantId }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/auth/block/${userId}`,
        {},
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getByRestaurantUsers = createAsyncThunk(
  "user/getByRestaurantUsers",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/auth/users`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async ({ userId, restaurantId }) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/auth/${userId}`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const assignUser = createAsyncThunk(
  "user/assignUser",
  async ({ userId, role, restaurantId }) => {
    try {
      const response = await axios.post(
        `${apiUrl}/auth/${userId}/assign`,
        { restaurantId, role },
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const unassignUser = createAsyncThunk(
  "user/unassignUser",
  async ({ userId, restaurantId }) => {
    try {
      const response = await axios.delete(`${apiUrl}/auth/${userId}/unassign`, {
        data: { restaurantId },
        ...getAuthHeaders(false, restaurantId),
      });
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const fetchUnassignUser = createAsyncThunk(
  "user/fetchUnassignUser",
  async (restaurantId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/auth/users/assignable?unassigned=true`,
        getAuthHeaders(false, restaurantId)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getAllUsers = createAsyncThunk(
  "user/getAllUsers",
  async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/auth`,
        getAuthHeaders(false)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const getUserRestaurants = createAsyncThunk(
  "user/getUserRestaurants",
  async (userId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/auth/${userId}/restaurants`,
        getAuthHeaders(false)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

export const addUserPermission = createAsyncThunk(
  "user/addUserPermission",
  async ({ userId, body }) => {
    try {
      const response = await axios.put(
        `${apiUrl}/auth/${userId}/notif`,
        { restaurantIds: body },
        getAuthHeaders(false)
      );
      return response?.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message);
    }
  }
);

const authSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateStatus: (state) => {
      state.status = "idle";
    },
  },
  extraReducers(builder) {
    builder
      .addCase(addUser.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyUser.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyUser.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(modifyUser.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getByRestaurantUsers.pending, (state, action) => {
        state.status = "loadingFetchData";
        state.loading = true;
      })
      .addCase(getByRestaurantUsers.fulfilled, (state, action) => {
        state.status = "fetchData";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getByRestaurantUsers.rejected, (state, action) => {
        state.status = "error";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteUser.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = "deleteSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = "error";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(blockUser.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.status = "blockSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(blockUser.rejected, (state, action) => {
        state.status = "error";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(assignUser.pending, (state) => {
        state.status = "assigning";
        state.loading = true;
      })
      .addCase(assignUser.fulfilled, (state, action) => {
        state.status = "assignSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(assignUser.rejected, (state, action) => {
        state.status = "assignError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(unassignUser.pending, (state) => {
        state.status = "unassigning";
        state.loading = true;
      })
      .addCase(unassignUser.fulfilled, (state, action) => {
        state.status = "unassignSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(unassignUser.rejected, (state, action) => {
        state.status = "unassignError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchUnassignUser.pending, (state) => {
        state.status = "fetchAssigning";
        state.loading = true;
      })
      .addCase(fetchUnassignUser.fulfilled, (state, action) => {
        state.status = "fetchAssignSuccess";
        state.loading = false;
        state.unAssignedUsers = action.payload;
      })
      .addCase(fetchUnassignUser.rejected, (state, action) => {
        state.status = "fetchAssignError";
        state.loading = false;
        state.error = action.error.message;
      }).addCase(getAllUsers.pending, (state) => {
        state.status = "loadingFetchAllUsers";
        state.loading = true;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.status = "fetchAllUsersSuccess";
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.status = "fetchAllUsersError";
        state.loading = false;
        state.error = action.error.message;
      }).addCase(getUserRestaurants.pending, (state) => {
        state.status = "loadingUserRestaurants";
        state.loading = true;
      })
      .addCase(getUserRestaurants.fulfilled, (state, action) => {
        state.status = "fetchUserRestaurantsSuccess";
        state.loading = false;
        state.userRestaurants = action.payload;
      })
      .addCase(getUserRestaurants.rejected, (state, action) => {
        state.status = "fetchUserRestaurantsError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addUserPermission.pending, (state) => {
        state.status = "loadingAddUserPermission";
        state.loading = true;
      })
      .addCase(addUserPermission.fulfilled, (state, action) => {
        state.status = "addUserPermissionSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addUserPermission.rejected, (state, action) => {
        state.status = "addUserPermissionError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addUserFromPermission.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(addUserFromPermission.fulfilled, (state, action) => {
        state.status = "addSuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(addUserFromPermission.rejected, (state, action) => {
        state.status = "addError";
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(modifyUserFromPermission.pending, (state, action) => {
        state.status = "loading";
        state.loading = true;
      })
      .addCase(modifyUserFromPermission.fulfilled, (state, action) => {
        state.status = "modifySuccess";
        state.loading = false;
        state.success = action.payload.message;
      })
      .addCase(modifyUserFromPermission.rejected, (state, action) => {
        state.status = "modifyError";
        state.loading = false;
        state.error = action.error.message;
      })
  },
});

export const { updateStatus } = authSlice.actions;
export const selectAllUsers = (state) => state.user.items;
export const selectUnAssignedUsers = (state) => state.user.unAssignedUsers;
export const selectUserRestaurants = (state) => state.user.userRestaurants;
export const getUserStatus = (state) => state.user.status;
export const getUserError = (state) => state.user.error;
export const getUserSuccess = (state) => state.user.success;
export const getUserLoading = (state) => state.user.loading;
export default authSlice.reducer;
